import React, { useEffect, useState, useCallback } from 'react';
import { Box, Text } from 'grommet';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { formatPhoneNumber } from 'react-phone-number-input';
import { AuthError } from 'firebase/auth';
import { MoonLoader } from 'react-spinners';
import SHDOTPInput from '@ui/OTPInput/SHDOTPInput';
import SHDButton from '@ui/Button/SHDButton';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDText from '@ui/Text/SHDText';
import { strogging } from '@shd/jslib/infra';

interface Props extends RouteComponentProps {
  phoneNumber: string;
  onVerify: (
    verificationCode: string
  ) => Promise<{ success: boolean; error?: AuthError }>;
  onResend: () => void;
}

export const OTPVerification: React.FC<Props> = ({
  phoneNumber,
  onVerify,
  onResend,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const [lastResendTime, setLastResendTime] = useState(Date.now());
  const [resendTimeLeft, setResendTimeLeft] = useState(60);
  const [showTimeLeft, setShowTimeLeft] = useState(false);

  const updateResendTimeLeft = useCallback(() => {
    const timeElapsed = (Date.now() - lastResendTime) / 1000;
    const timeLeft = Math.max(0, 60 - timeElapsed);
    setResendTimeLeft(Math.floor(timeLeft));
    if (timeLeft === 0) {
      setShowTimeLeft(false);
    }
  }, [lastResendTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateResendTimeLeft();
    }, 1000);
    return () => clearInterval(interval);
  }, [lastResendTime, updateResendTimeLeft]);

  const handleVerify = async () => {
    setVerifyLoading(true);
    const result = await onVerify(verificationCode);
    if (result.success) {
      setVerifyLoading(false);
    } else if (result.error) {
      setError(result.error);
      setVerifyLoading(false);
    }
  };

  const handleResendCode = () => {
    if (resendTimeLeft > 0) {
      setShowTimeLeft(true);
    } else {
      setResendLoading(true);
      onResend();
      setLastResendTime(Date.now());
      setTimeout(() => {
        setResendLoading(false);
      }, 1500);
      setShowTimeLeft(false);
    }
  };

  const handleButtonError = (err: string) => {
    strogging.log('Button error:', err);
  };

  return (
    <>
      <Box gap="small" margin={{ bottom: 'xlarge' }}>
        <SHDHeading className="otp-verification" level={3}>
          Verify your mobile number
        </SHDHeading>

        <SHDText style="subhead" color="gray-900">
          Enter the 6 digit verification code we sent you at:{' '}
          {formatPhoneNumber(phoneNumber)}. This code is valid for 5 minutes.
        </SHDText>
      </Box>
      <Box margin={{ bottom: 'xxlarge' }}>
        <SHDOTPInput error={error} onChange={setVerificationCode} />
      </Box>
      <Box>
        <SHDButton
          primary
          label={verifyLoading ? <Text color={'transparent'}></Text> : 'Verify'}
          disabled={verificationCode.length !== 6 || verifyLoading}
          onClick={handleVerify}
          icon={verifyLoading ? <MoonLoader size={20} /> : undefined}
          fill
          onError={({ message }) => handleButtonError(message)}
        />
        <SHDButton
          label={resendLoading ? 'Resending...' : 'Resend code'}
          disabled={resendLoading}
          onClick={handleResendCode}
          onError={({ message }) => handleButtonError(message)}
        />
        {showTimeLeft && resendTimeLeft > 0 && (
          <Text>You can resend code in {resendTimeLeft} seconds</Text>
        )}
      </Box>
    </>
  );
};

export default withRouter(OTPVerification);
